import { BoardPackSchedule } from "../components/BoardPackSchedule";
import { runAiJob, pollAiJob, rememberAiJob, forgetAiJob, recalledAiJobs } from "../lib/aiJob";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type React from "react";
import { PortalLink } from "../components/PortalLink";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { theme, card, btnPrimary, emptyFrame } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { IndexTabs, StatusTag, type StatusTone } from "../components/Drawing";
import { api, ApiError } from "../api/client";
import { TierLockModal, TierErrorPayload, TIER_NAMES } from "../components/TierLockModal";
import { ExecutiveReportDashboard } from "../components/ExecutiveReportDashboard";
import type { ExecutiveReportView, ExecutiveReportListItem } from "../types/executiveReport";
import { resolveExportPalette, type ExportBrandSource } from "../lib/exportBrand";
import { useCurrentUser } from "../auth/useCurrentUser";
import { frameworkLabel } from "../nav/frameworkMeta";
import { displayControlRef, displayControlTitle } from "../lib/controlRef";
import { markdownToPlainText } from "../lib/markdownPlain";

import { cbPalette, cbSafe } from "../lib/colourBlind";
interface CheckRun {
  id: string;
  status: string;
  overallScore: number | null;
  completedAt: string | null;
}

interface ReportSection {
  heading: string;
  body: string;
}

interface SoaItem {
  control: { id: string; frameworkId: string; name?: string };
  entry: { status: string; notes?: string; applicable?: boolean | null };
}

const FW_LABELS: Record<string, string> = {
  iso27001: "ISO 27001:2022",
  iso9001: "ISO 9001:2015",
  iso42001: "ISO 42001:2023",
  soc2: "SOC 2",
  cyber_essentials: "Cyber Essentials",
  cyber_essentials_plus: "Cyber Essentials Plus",
  pci_dss: "PCI DSS v4.0",
  nist_csf: "NIST CSF 2.0",
  iso45001: "ISO 45001:2018",
  ncsc_caf: "NCSC CAF v4.0",
  gdpr: "GDPR",
  uk_gdpr: "UK GDPR",
  jersey_dpjl: "Jersey DPJL 2018",
  iasme_ca_sole_trader: "IASME CA (Sole Trader)",
  iasme_ca_micro: "IASME CA (Micro)",
  iasme_ca_small: "IASME CA (Small)",
  iasme_ca_medium_large: "IASME CA (Medium/Large)",
};

const STATUS_COLORS: Record<string, string> = cbPalette({
  COMPLIANT: "#22c55e",
  IN_PROGRESS: "#eab308",
  GAP: "#ef4444",
  NOT_STARTED: "#94a3b8",
});

const STATUS_LABELS: Record<string, string> = {
  COMPLIANT: "Compliant",
  IN_PROGRESS: "In progress",
  GAP: "Gap",
  NOT_STARTED: "Not started",
};

const STATUS_TONES: Record<string, StatusTone> = {
  COMPLIANT: "met",
  IN_PROGRESS: "partial",
  GAP: "gap",
  NOT_STARTED: "none",
};

// Minimal cross-framework topic map used for cross-impact section.
// fwKeys lists the frameworkIds (as stored in SOA) that address each topic.
const CROSS_FW_TOPICS: Array<{ topic: string; category: string; fwKeys: string[] }> = [
  { topic: "Access Control Policy",         category: "Access Control",       fwKeys: ["iso27001","cyber_essentials","cyber_essentials_plus","iso42001","soc2","pci_dss","nist_csf","gdpr","uk_gdpr","iasme_ca_sole_trader","iasme_ca_micro","iasme_ca_small","iasme_ca_medium_large"] },
  { topic: "Multi-factor Authentication",   category: "Access Control",       fwKeys: ["iso27001","cyber_essentials","cyber_essentials_plus","soc2","pci_dss"] },
  { topic: "Privileged Access Management",  category: "Access Control",       fwKeys: ["iso27001","cyber_essentials","cyber_essentials_plus","iso42001","soc2","pci_dss"] },
  { topic: "Password / Credential Policy",  category: "Access Control",       fwKeys: ["iso27001","cyber_essentials","cyber_essentials_plus","soc2","pci_dss","gdpr","uk_gdpr"] },
  { topic: "Firewall & Network Boundary",   category: "Network Security",     fwKeys: ["iso27001","cyber_essentials","cyber_essentials_plus","soc2","pci_dss"] },
  { topic: "Malware / Antivirus Protection",category: "Endpoint Security",    fwKeys: ["iso27001","cyber_essentials","cyber_essentials_plus","soc2","pci_dss"] },
  { topic: "Patch Management & Updates",    category: "Endpoint Security",    fwKeys: ["iso27001","cyber_essentials","cyber_essentials_plus","soc2","pci_dss","nist_csf"] },
  { topic: "Secure Configuration",          category: "Endpoint Security",    fwKeys: ["iso27001","cyber_essentials","cyber_essentials_plus","soc2","pci_dss"] },
  { topic: "Cryptography & Encryption",     category: "Data Protection",      fwKeys: ["iso27001","soc2","pci_dss","gdpr","uk_gdpr"] },
  { topic: "Data Classification",           category: "Data Protection",      fwKeys: ["iso27001","iso42001","soc2","pci_dss","gdpr","uk_gdpr"] },
  { topic: "Data Retention & Disposal",     category: "Data Protection",      fwKeys: ["iso27001","iso42001","iso9001","soc2","pci_dss","gdpr","uk_gdpr"] },
  { topic: "Backup & Recovery",             category: "Business Continuity",  fwKeys: ["iso27001","iso9001","soc2","pci_dss","gdpr","uk_gdpr"] },
  { topic: "Incident Detection & Response", category: "Incident Management",  fwKeys: ["iso27001","iso42001","iso9001","soc2","pci_dss","gdpr","uk_gdpr"] },
  { topic: "Security Awareness Training",   category: "People Security",      fwKeys: ["iso27001","iso42001","iso9001","soc2","pci_dss","nist_csf"] },
  { topic: "Supplier / Third-party Risk",   category: "Supply Chain",         fwKeys: ["iso27001","iso42001","iso9001","soc2","pci_dss","gdpr","uk_gdpr"] },
  { topic: "Vulnerability Management",      category: "Application Security", fwKeys: ["iso27001","cyber_essentials","cyber_essentials_plus","soc2","pci_dss","nist_csf"] },
  { topic: "Audit Logging & Monitoring",    category: "Monitoring",           fwKeys: ["iso27001","iso42001","iso9001","soc2","pci_dss","gdpr","uk_gdpr","nist_csf"] },
  { topic: "Risk Assessment",               category: "Governance",           fwKeys: ["iso27001","iso42001","iso9001","soc2","pci_dss","gdpr","uk_gdpr","nist_csf"] },
  { topic: "Asset Inventory",               category: "Asset Management",     fwKeys: ["iso27001","iso42001","iso9001","soc2","pci_dss","gdpr","uk_gdpr"] },
  { topic: "Policy Management",             category: "Governance",           fwKeys: ["iso27001","iso42001","iso9001","soc2","pci_dss","nist_csf"] },
];

// ── Inline bold parser ────────────────────────────────────────────────────────

function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  if (parts.length === 1) return text;
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i} style={{ fontWeight: 700, color: theme.color.heading }}>
            {part.slice(2, -2)}
          </strong>
        ) : (
          part
        )
      )}
    </>
  );
}

// ── Table helpers ─────────────────────────────────────────────────────────────

function isTableSep(line: string): boolean {
  return /^[|\s\-:]+$/.test(line) && line.includes("|") && line.includes("-");
}

function parseTableRow(line: string): string[] {
  return line
    .split("|")
    .map((c) => c.trim())
    .filter((_, i, arr) => i > 0 && i < arr.length - 1);
}

function renderTable(tableLines: string[], key: string | number): React.ReactNode {
  const rows = tableLines.filter((l) => !isTableSep(l)).map(parseTableRow);
  if (rows.length === 0) return null;
  const [header, ...body] = rows;
  return (
    <div key={key} style={{ overflowX: "auto", margin: "10px 0" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            {header.map((cell, ci) => (
              <th
                key={ci}
                style={{
                  padding: "7px 10px",
                  textAlign: "left",
                  fontWeight: 700,
                  color: theme.color.heading,
                  background: theme.color.panelAlt,
                  borderBottom: "1px solid var(--c-line)",
                  whiteSpace: "nowrap",
                }}
              >
                {renderInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  style={{ padding: "7px 10px", color: theme.color.text, verticalAlign: "top" }}
                >
                  {renderInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Body renderer ─────────────────────────────────────────────────────────────

function renderBody(text: string): React.ReactNode[] {
  const lines = text.split("\n");
  const result: React.ReactNode[] = [];
  let listItems: string[] = [];
  let tableLines: string[] = [];

  function flushList() {
    if (!listItems.length) return;
    result.push(
      <ul key={`list-${result.length}`} style={{ margin: "6px 0 8px", paddingLeft: 18, listStyle: /^\d+[.)]\s/.test(listItems[0]) ? "none" : undefined }}>
        {listItems.map((item, i) => {
          const numbered = /^(\d+)[.)]\s+(.*)$/.exec(item);
          return (
            <li key={i} style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.7, marginBottom: 2, ...(numbered ? { marginLeft: -18 } : {}) }}>
              {numbered && <span style={{ display: "inline-block", minWidth: 18, fontWeight: 700, color: theme.color.accent }}>{numbered[1]}.</span>}
              {renderInline(numbered ? numbered[2] : item)}
            </li>
          );
        })}
      </ul>
    );
    listItems = [];
  }

  function flushTable() {
    if (!tableLines.length) return;
    const node = renderTable(tableLines, `tbl-${result.length}`);
    if (node) result.push(node);
    tableLines = [];
  }

  lines.forEach((line, i) => {
    const trimmed = line.trim();

    // Skip markdown dividers
    if (/^[-*_]{3,}$/.test(trimmed)) return;

    // Table lines
    if (trimmed.startsWith("|")) {
      flushList();
      tableLines.push(trimmed);
      return;
    } else {
      flushTable();
    }

    if (!trimmed) {
      flushList();
      return;
    }

    if (/^#{3,4}\s/.test(trimmed)) {
      flushList();
      const heading = trimmed.replace(/^#{3,4}\s*/, "");
      result.push(
        <div
          key={i}
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: theme.color.heading,
            marginTop: 14,
            marginBottom: 4,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {renderInline(heading)}
        </div>
      );
    } else if (/^[-*]\s/.test(trimmed) || /^\[\s*\]\s/.test(trimmed)) {
      listItems.push(trimmed.replace(/^[-*]\s*/, "").replace(/^\[\s*\]\s*/, ""));
    } else if (/^\d+[.)]\s/.test(trimmed)) {
      listItems.push(trimmed);
    } else if (/^>\s?/.test(trimmed)) {
      flushList();
      result.push(
        <div key={i} style={{ margin: "8px 0", padding: "8px 12px", borderLeft: `3px solid ${theme.color.warning}`, background: theme.color.warningBg, borderRadius: 3, fontSize: 13, color: theme.color.warningText, lineHeight: 1.6 }}>
          {renderInline(trimmed.replace(/^>\s?/, ""))}
        </div>
      );
    } else {
      flushList();
      result.push(
        <p key={i} style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.8, margin: "5px 0" }}>
          {renderInline(trimmed)}
        </p>
      );
    }
  });

  flushList();
  flushTable();
  return result;
}

// ── Markdown section parser ───────────────────────────────────────────────────

function parseReport(text: string): ReportSection[] {
  const parts = text.split(/\n(?=##\s)/);
  return parts
    .map((part) => {
      const lines = part.trim().split("\n");
      const headingLine = lines[0].replace(/^#+\s*/, "").replace(/\*\*/g, "").trim();
      const body = lines.slice(1).join("\n").trim();
      return { heading: headingLine, body };
    })
    .filter((s) => s.heading);
}

// ── PDF helpers ───────────────────────────────────────────────────────────────

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^#{1,6}\s*/gm, "")
    .trim();
}

// ── Gap Analysis Tab ─────────────────────────────────────────────────────────

function GapAnalysisTab({
  soaData,
  soaLoading,
  soaError,
  enrolledFrameworkIds,
  onDownloadPdf,
}: {
  soaData: SoaItem[] | null;
  soaLoading: boolean;
  soaError: string | null;
  enrolledFrameworkIds: Set<string> | null;
  onDownloadPdf: () => void;
}) {
  if (soaLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: theme.color.subtle, padding: "40px 0" }}>
        <span style={{ display: "inline-block", width: 14, height: 14, border: `2px solid ${theme.color.accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        Loading SOA data…
      </div>
    );
  }

  if (soaError) {
    return (
      <div style={{ padding: "14px 18px", background: cbSafe("#dc262622"), border: cbSafe("1px solid #7f1d1d"), borderRadius: 3, fontSize: 13, color: theme.color.dangerText }}>
        {soaError}
      </div>
    );
  }

  if (!soaData || soaData.length === 0) {
    return (
      <div style={{ ...emptyFrame, padding: "40px 24px" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>No SOA data found</div>
        <div style={{ fontSize: 13, color: theme.color.subtle }}>Complete your Statement of Applicability to view gap analysis.</div>
      </div>
    );
  }

  // ── Group by framework, restricted to enrolled ones ──
  const byFw = new Map<string, SoaItem[]>();
  for (const item of soaData) {
    const fw = item.control.frameworkId;
    if (enrolledFrameworkIds && !enrolledFrameworkIds.has(fw)) continue;
    if (!byFw.has(fw)) byFw.set(fw, []);
    byFw.get(fw)!.push(item);
  }

  const frameworks = [...byFw.keys()].sort();
  const enrolledSet = new Set(frameworks);

  // ── Cross-framework impact ──
  const fwsWithGaps = new Set(
    frameworks.filter((fw) =>
      (byFw.get(fw) ?? []).some((i) => i.entry.status === "GAP" || i.entry.status === "NOT_STARTED")
    )
  );

  const crossImpact = CROSS_FW_TOPICS
    .map((t) => {
      const overlapping = t.fwKeys.filter((k) => enrolledSet.has(k) && fwsWithGaps.has(k));
      return { ...t, overlapping };
    })
    .filter((t) => t.overlapping.length >= 2)
    .sort((a, b) => b.overlapping.length - a.overlapping.length);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 13, color: theme.color.subtle }}>
          Showing gaps across {frameworks.length} enrolled framework{frameworks.length !== 1 ? "s" : ""}.
        </div>
        <button onClick={onDownloadPdf} style={{ ...btnPrimary, fontSize: 12, padding: "8px 16px" }}>
          Download Gap Analysis PDF
        </button>
      </div>

      {/* Per-framework sections */}
      {frameworks.map((fw) => {
        const items = byFw.get(fw) ?? [];
        const total = items.length;
        const counts = { COMPLIANT: 0, IN_PROGRESS: 0, GAP: 0, NOT_STARTED: 0 };
        for (const i of items) {
          const s = i.entry.status as keyof typeof counts;
          if (s in counts) counts[s]++;
        }
        const gapItems = items.filter((i) => i.entry.status === "GAP" || i.entry.status === "NOT_STARTED");
        const label = FW_LABELS[fw] ?? frameworkLabel(fw);

        return (
          <div key={fw} style={card}>
            {/* Framework heading */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{label}</div>
              <div style={{ fontSize: 12, color: counts.GAP > 0 ? theme.color.dangerText : theme.color.subtle }}>
                {counts.GAP + counts.NOT_STARTED} gap{counts.GAP + counts.NOT_STARTED !== 1 ? "s" : ""} out of {total} control{total !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ height: 10, background: theme.color.border, borderRadius: 3, overflow: "hidden", marginBottom: 8, display: "flex" }}>
              {(["COMPLIANT","IN_PROGRESS","GAP","NOT_STARTED"] as const).map((s) => {
                const pct = total > 0 ? (counts[s] / total) * 100 : 0;
                return pct > 0 ? (
                  <div key={s} style={{ width: `${pct}%`, background: STATUS_COLORS[s], transition: "width 0.3s" }} title={`${STATUS_LABELS[s]}: ${counts[s]}`} />
                ) : null;
              })}
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: 14, marginBottom: gapItems.length > 0 ? 16 : 0, flexWrap: "wrap" }}>
              {(["COMPLIANT","IN_PROGRESS","GAP","NOT_STARTED"] as const).map((s) => counts[s] > 0 && (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_COLORS[s] }} />
                  <span style={{ fontSize: 11, color: theme.color.subtle }}>{STATUS_LABELS[s]}: {counts[s]}</span>
                </div>
              ))}
            </div>

            {/* Gap table */}
            {gapItems.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {["Control ID","Control Name","Status","Notes"].map((h) => (
                        <th key={h} style={{ padding: "7px 10px", textAlign: "left", fontWeight: 700, color: theme.color.heading, background: theme.color.panelAlt, borderBottom: "1px solid var(--c-line)", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {gapItems.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                        <td style={{ padding: "7px 10px", color: theme.color.muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{displayControlRef(item.control.id)}</td>
                        <td style={{ padding: "7px 10px", color: theme.color.text }}>{item.control.name ? displayControlTitle(item.control.id, item.control.name) : "—"}</td>
                        <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                          <StatusTag tone={STATUS_TONES[item.entry.status] ?? "none"}>
                            {STATUS_LABELS[item.entry.status] ?? item.entry.status}
                          </StatusTag>
                        </td>
                        <td style={{ padding: "7px 10px", color: theme.color.subtle }}>
                          {item.entry.notes ? (item.entry.notes.length > 60 ? item.entry.notes.slice(0, 60) + "…" : item.entry.notes) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {gapItems.length === 0 && (
              <div style={{ fontSize: 12, color: cbSafe("#22c55e"), fontWeight: 600 }}>No gaps — all controls are compliant or in progress.</div>
            )}
          </div>
        );
      })}

      {/* Cross-framework impact */}
      {crossImpact.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
            Controls with cross-framework impact — address these first
          </div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
            Topics below have gaps in {crossImpact[0]?.overlapping.length ?? 2}+ of your enrolled frameworks. Fixing them delivers the broadest compliance improvement.
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Topic","Category","Frameworks affected"].map((h) => (
                    <th key={h} style={{ padding: "7px 10px", textAlign: "left", fontWeight: 700, color: theme.color.heading, background: theme.color.panelAlt, borderBottom: "1px solid var(--c-line)", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crossImpact.map((t, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                    <td style={{ padding: "7px 10px", color: theme.color.text, fontWeight: 600 }}>{t.topic}</td>
                    <td style={{ padding: "7px 10px", color: theme.color.subtle }}>{t.category}</td>
                    <td style={{ padding: "7px 10px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {t.overlapping.map((fw) => (
                          <span key={fw} style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 4, padding: "1px 6px", fontSize: 11, color: theme.color.muted }}>
                            {FW_LABELS[fw] ?? frameworkLabel(fw)}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {crossImpact.length === 0 && frameworks.length > 0 && (
        <div style={{ ...emptyFrame, padding: "20px 24px" }}>
          <div style={{ fontSize: 13, color: theme.color.subtle }}>No cross-framework gaps detected across your enrolled standards.</div>
        </div>
      )}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const secondaryBtn: React.CSSProperties = {
  border: `1px solid ${theme.color.borderStrong}`,
  background: "transparent",
  color: theme.color.muted,
  borderRadius: theme.radius.sm,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

// The history rows follow the "Previous runs" list on Check My Compliance.
const historyRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 12px",
  flexWrap: "wrap",
  background: theme.color.panelAlt,
  border: `1px solid ${theme.color.border}`,
  borderRadius: 3,
};

const historyBadge: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  padding: "2px 8px",
  borderRadius: 3,
  whiteSpace: "nowrap",
};

const historyBtn: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  padding: "3px 10px",
  borderRadius: 3,
  border: `1px solid ${theme.color.borderStrong}`,
  background: "transparent",
  color: theme.color.muted,
  cursor: "pointer",
  flexShrink: 0,
};

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

// ── Main page component ───────────────────────────────────────────────────────

export function ExecutiveReportPage() {
  // Anyone may read the stored reports; generating one is reports:generate (compliance:run implies it).
  const { user } = useCurrentUser();
  const canGenerate = !!user?.permissions?.includes("reports:generate");
  const navigate = usePortalNavigate();
  const [runs, setRuns] = useState<CheckRun[] | null>(null);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState<ExecutiveReportView | null>(null);
  const [history, setHistory] = useState<ExecutiveReportListItem[]>([]);
  const [historyError, setHistoryError] = useState(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<{ id: string; format: "pdf" | "docx" } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tierError, setTierError] = useState<TierErrorPayload | null>(null);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // ── Gap analysis state ──
  // ?tab=gaps opens the gap analysis directly (the consultant client view links here).
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"summary" | "gaps">(searchParams.get("tab") === "gaps" ? "gaps" : "summary");
  const [soaData, setSoaData] = useState<SoaItem[] | null>(null);
  const [soaLoading, setSoaLoading] = useState(false);
  const [soaError, setSoaError] = useState<string | null>(null);
  const soaLoadedRef = useRef(false);
  const [enrolledFrameworkIds, setEnrolledFrameworkIds] = useState<Set<string> | null>(null);

  useEffect(() => {
    api
      .get<{ runs: CheckRun[] }>("/compliance/check-runs")
      .then((d) => setRuns(d.runs))
      .catch(() => setRuns([]));
    // Reports are stored: list them and show the most recent one straight away.
    void loadHistory(true);
    if (activeTab === "gaps") loadSoa();
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const hasCompletedRun = runs?.some((r) => r.status === "done") ?? false;

  // Every generated report is kept, so the history is the server's list (newest
  // first) — re-read after a generation so the new row names who generated it.
  async function loadHistory(openLatest = false) {
    try {
      const d = await api.get<{ reports: ExecutiveReportListItem[] }>("/compliance/executive-reports");
      setHistory(d.reports);
      setHistoryError(false);
      if (openLatest && d.reports[0]) void openReport(d.reports[0].id);
    } catch {
      setHistoryError(true);
    }
  }

  async function openReport(id: string) {
    setLoadingId(id);
    setError(null);
    try {
      setReport(await api.get<ExecutiveReportView>(`/compliance/executive-reports/${id}`));
    } catch {
      setError("That report could not be loaded.");
    } finally {
      setLoadingId((current) => (current === id ? null : current));
    }
  }

  const REPORT_JOB_KEY = "executive-report";
  const reportStatusPath = (jobId: string) => `/compliance/executive-report/jobs/${encodeURIComponent(jobId)}`;

  // The report is written on the worker; this waits for the job, which carries on if the page is left.
  async function followReport(wait: (signal: AbortSignal) => Promise<ExecutiveReportView>) {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setGenerating(true);
    setError(null);
    try {
      const result = await wait(controller.signal);
      setReport(result);
      setHistory((prev) => [{
        id: result.id, generatedAt: result.generatedAt, overallScore: result.overallScore, generatedBy: null,
        styledWithTemplate: result.styledWithTemplate, downloads: result.downloads, renderError: result.renderError,
      }, ...prev.filter((h) => h.id !== result.id)]);
      forgetAiJob(REPORT_JOB_KEY);
      void loadHistory();
    } catch (e) {
      if (controller.signal.aborted) return;
      forgetAiJob(REPORT_JOB_KEY);
      if (e instanceof ApiError && e.status === 402) {
        setTierError(e.payload as TierErrorPayload);
      } else if (e instanceof ApiError) {
        const payload = e.payload as { message?: string } | null;
        setError(payload?.message ?? "Failed to generate report. Please try again.");
      } else {
        setError(e instanceof Error && e.message ? e.message : "Failed to generate report. Please try again.");
      }
    } finally {
      if (!controller.signal.aborted) setGenerating(false);
    }
  }

  function generateReport() {
    return followReport((signal) => runAiJob<ExecutiveReportView>("/compliance/executive-report", reportStatusPath, {}, {
      signal,
      onJobId: (jobId) => rememberAiJob(REPORT_JOB_KEY, jobId),
    }));
  }

  // Picks up a report started before the page was left.
  useEffect(() => {
    const [saved] = recalledAiJobs(REPORT_JOB_KEY);
    if (saved) void followReport((signal) => pollAiJob<ExecutiveReportView>(saved.jobId, reportStatusPath, { signal, waitMs: saved.remainingMs }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stops waiting; a report already being written still finishes and appears in the history.
  function cancelGeneration() {
    abortRef.current?.abort();
    forgetAiJob(REPORT_JOB_KEY);
    setGenerating(false);
  }

  // The rendered files live in Blob Storage; the API hands out a short-lived URL
  // with the filename set, so the browser saves e.g. "Contoso-Executive-Report-2026-09-19.pdf".
  async function download(format: "pdf" | "docx", id = report?.id) {
    if (!id) return;
    setDownloading({ id, format });
    try {
      const { url } = await api.get<{ url: string }>(`/compliance/executive-reports/${id}/download-url`, { format });
      const a = document.createElement("a");
      a.href = url;
      a.download = "";
      a.click();
    } catch {
      setError(`The ${format === "pdf" ? "PDF" : "Word document"} is not available for this report.`);
    } finally {
      setDownloading(null);
    }
  }

  function loadSoa() {
    if (soaLoadedRef.current) return;
    soaLoadedRef.current = true;
    setSoaLoading(true);
    setSoaError(null);
    Promise.all([
      api.get<{ soa: SoaItem[] }>("/controls/soa"),
      api.get<{ frameworks: { frameworkId: string }[] }>("/organizations/frameworks"),
    ])
      .then(([soaRes, fwRes]) => {
        // A control marked not applicable is out of scope, never a gap (the board report and the
        // sidebar count it separately), so it is left out of the analysis and its PDF.
        setSoaData(soaRes.soa.filter((i) => i.entry.applicable !== false));
        setEnrolledFrameworkIds(new Set(fwRes.frameworks.map((f) => f.frameworkId)));
      })
      .catch(() => setSoaError("Failed to load SOA data. Please try again."))
      .finally(() => setSoaLoading(false));
  }

  async function downloadGapPdf() {
    if (!soaData) return;
    // The organisation's own brand colours when it set them in My Organisation,
    // the Abacas palette otherwise — the same resolution the API applies to every
    // document it renders (lib/exportBrand.ts).
    const profile = await api
      .get<{ organization: ExportBrandSource & { name?: string } }>("/organizations/profile")
      .then((r) => r.organization)
      .catch(() => null);
    const brand = resolveExportPalette(profile);
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentW = pageW - margin * 2;
    let y = margin;

    // The profile names the organisation even before any board report exists.
    const orgName = report?.orgName ?? profile?.name ?? "Organisation";
    const dateStr = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

    // ── Title page ──
    doc.setFillColor(...brand.heading);
    doc.rect(0, 0, pageW, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text(orgName, margin, 12);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("MULTI-FRAMEWORK GAP ANALYSIS  |  CONFIDENTIAL", margin, 20);
    doc.text(dateStr, pageW - margin, 20, { align: "right" });
    y = 42;

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...brand.heading);
    doc.text("Multi-Framework Gap Analysis", margin, y);
    y += 8;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...brand.muted);
    doc.text(`${orgName}  —  ${dateStr}`, margin, y);
    y += 14;

    // ── Group by framework ──
    const byFw = new Map<string, SoaItem[]>();
    for (const item of soaData) {
      const fw = item.control.frameworkId;
      if (!byFw.has(fw)) byFw.set(fw, []);
      byFw.get(fw)!.push(item);
    }
    const frameworks = [...byFw.keys()].sort();
    const enrolledSet = new Set(frameworks);

    for (const fw of frameworks) {
      const items = byFw.get(fw) ?? [];
      const total = items.length;
      const counts = { COMPLIANT: 0, IN_PROGRESS: 0, GAP: 0, NOT_STARTED: 0 };
      for (const i of items) {
        const s = i.entry.status as keyof typeof counts;
        if (s in counts) counts[s]++;
      }
      const gapItems = items.filter((i) => i.entry.status === "GAP" || i.entry.status === "NOT_STARTED");
      const label = FW_LABELS[fw] ?? frameworkLabel(fw);

      if (y > pageH - 50) { doc.addPage(); y = margin; }

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...brand.heading);
      doc.text(label, margin, y);
      y += 2;
      doc.setDrawColor(...brand.accent);
      doc.setLineWidth(0.4);
      doc.line(margin, y, margin + contentW, y);
      y += 5;

      doc.setFontSize(8.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...brand.muted);
      doc.text(
        `${counts.GAP + counts.NOT_STARTED} gap${counts.GAP + counts.NOT_STARTED !== 1 ? "s" : ""} out of ${total} controls  |  Compliant: ${counts.COMPLIANT}  In progress: ${counts.IN_PROGRESS}  Gap: ${counts.GAP}  Not started: ${counts.NOT_STARTED}`,
        margin, y
      );
      y += 8;

      if (gapItems.length === 0) {
        doc.setTextColor(34, 197, 94);
        doc.text("No gaps — all controls are compliant or in progress.", margin, y);
        y += 10;
        continue;
      }

      // Table header
      const colWidths = [38, 62, 24, contentW - 38 - 62 - 24];
      const headers = ["Control ID", "Control Name", "Status", "Notes"];
      doc.setFillColor(...brand.tile);
      doc.rect(margin, y - 4, contentW, 8, "F");
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...brand.heading);
      let cx = margin;
      headers.forEach((h, i) => { doc.text(h, cx + 1, y); cx += colWidths[i]; });
      y += 5;
      doc.setLineWidth(0.3);
      doc.setDrawColor(...brand.rule);
      doc.line(margin, y, margin + contentW, y);
      y += 3;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      for (const item of gapItems) {
        if (y > pageH - 18) { doc.addPage(); y = margin; }
        const statusLabel = STATUS_LABELS[item.entry.status] ?? item.entry.status;
        const notes = item.entry.notes ? (item.entry.notes.length > 50 ? item.entry.notes.slice(0, 50) + "…" : item.entry.notes) : "—";
        const cells = [displayControlRef(item.control.id), item.control.name ? displayControlTitle(item.control.id, item.control.name) : "—", statusLabel, notes];
        cx = margin;
        doc.setTextColor(...brand.text);
        cells.forEach((cell, i) => {
          const wrapped = doc.splitTextToSize(String(cell), colWidths[i] - 2);
          doc.text(wrapped[0] ?? "", cx + 1, y);
          cx += colWidths[i];
        });
        y += 5;
        doc.setDrawColor(...brand.rule);
        doc.line(margin, y, margin + contentW, y);
        y += 2;
      }
      y += 6;
    }

    // ── Cross-framework impact ──
    const fwsWithGaps = new Set(
      frameworks.filter((fw) =>
        (byFw.get(fw) ?? []).some((i) => i.entry.status === "GAP" || i.entry.status === "NOT_STARTED")
      )
    );
    const crossImpact = CROSS_FW_TOPICS
      .map((t) => ({ ...t, overlapping: t.fwKeys.filter((k) => enrolledSet.has(k) && fwsWithGaps.has(k)) }))
      .filter((t) => t.overlapping.length >= 2)
      .sort((a, b) => b.overlapping.length - a.overlapping.length);

    if (crossImpact.length > 0) {
      if (y > pageH - 50) { doc.addPage(); y = margin; }
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...brand.heading);
      doc.text("Controls with cross-framework impact — address these first", margin, y);
      y += 2;
      doc.setDrawColor(...brand.accent);
      doc.setLineWidth(0.4);
      doc.line(margin, y, margin + contentW, y);
      y += 8;

      for (const t of crossImpact) {
        if (y > pageH - 18) { doc.addPage(); y = margin; }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...brand.text);
        doc.text(`${t.topic}  (${t.category})`, margin, y);
        y += 4.5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...brand.muted);
        const fwLine = t.overlapping.map((fw) => FW_LABELS[fw] ?? frameworkLabel(fw)).join(", ");
        const wrapped = doc.splitTextToSize(fwLine, contentW);
        for (const wl of wrapped) { doc.text(wl, margin + 4, y); y += 4; }
        y += 3;
      }
    }

    // ── Footer ──
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFontSize(7);
      doc.setTextColor(...brand.muted);
      doc.text(orgName, margin, pageH - 8);
      doc.text(`Page ${p} of ${totalPages}`, pageW - margin, pageH - 8, { align: "right" });
      doc.text("CONFIDENTIAL", pageW / 2, pageH - 8, { align: "center" });
    }

    doc.save(`${orgName.replace(/\s+/g, "-").toLowerCase()}-gap-analysis.pdf`);
  }

  async function copyToClipboard() {
    if (!report) return;
    // Plain text: the button is for pasting into an email or a board paper, where Markdown marks show.
    await navigator.clipboard.writeText(markdownToPlainText(report.report));
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  if (runs === null) {
    return <LoadingBlock />;
  }

  const sections = report ? parseReport(report.report) : [];

  return (
    <div className="page-pad">
      {tierError && (
        <TierLockModal
          featureName="Executive Report"
          requiredTierName={TIER_NAMES[tierError.requiredLevel] ?? `tier ${tierError.requiredLevel}`}
          requiredTierLevel={tierError.requiredLevel}
          currentTierLevel={tierError.currentLevel}
          onClose={() => setTierError(null)}
          onRequestUpgrade={() => navigate("/my-services")}
        />
      )}

      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Board report
      </h1>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: theme.color.subtle }}>
        A professional executive summary of your compliance position across all enrolled frameworks,
        based on your latest AI check results.
      </p>

      {/* Tab switcher */}
      <IndexTabs
        style={{ marginBottom: 24 }}
        value={activeTab}
        onChange={(tab) => { setActiveTab(tab); if (tab === "gaps") loadSoa(); }}
        items={[
          { id: "summary", label: "Executive Summary" },
          { id: "gaps", label: "Gap Analysis" },
        ]}
      />

      {activeTab === "summary" && (
        <>
          {/* No check run yet */}
      {!hasCompletedRun && (
        <div
          style={{
            ...emptyFrame,
            padding: "40px 24px",
            marginBottom: 20,
          }}
        >
          <div style={{ fontSize: 14, color: theme.color.heading, fontWeight: 600, marginBottom: 8 }}>
            No compliance check run yet
          </div>
          <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 18, lineHeight: 1.6 }}>
            Run a compliance check first from 'Check My Compliance' to generate an executive report.
          </div>
          <PortalLink to="/compliance" style={{ ...btnPrimary, textDecoration: "none", display: "inline-block" }}>
            Go to Check My Compliance
          </PortalLink>
        </div>
      )}

      {/* Has completed run */}
      {hasCompletedRun && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
                {report ? "Executive report" : "Ready to generate"}
              </div>
              <div style={{ fontSize: 12, color: theme.color.subtle }}>
                {report
                  ? `Generated ${fmtWhen(report.generatedAt)} — ${report.orgName}`
                  : "Uses your most recent completed compliance check, the risk register, suppliers, legal obligations, certification and evidence."}
              </div>
              {report && (
                <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 2, background: report.styledWithTemplate ? theme.color.successBg : theme.color.panelAlt, color: report.styledWithTemplate ? theme.color.successText : theme.color.subtle, border: `1px solid ${theme.color.border}` }}>
                    {report.styledWithTemplate ? "Rendered in your document template" : "Standard style"}
                  </span>
                  {!report.styledWithTemplate && (
                    <PortalLink to="/org-profile" style={{ fontSize: 11, color: theme.color.accent }}>Upload a Word template to brand it</PortalLink>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
              {report && (
                <>
                  <button onClick={copyToClipboard} style={secondaryBtn}>
                    {copied ? "Copied!" : "Copy text"}
                  </button>
                  <button onClick={() => download("pdf")} disabled={!report.downloads.pdf || downloading !== null} style={{ ...secondaryBtn, opacity: report.downloads.pdf ? 1 : 0.5 }}>
                    {downloading?.id === report.id && downloading.format === "pdf" ? "Preparing…" : "Download PDF"}
                  </button>
                  <button onClick={() => download("docx")} disabled={!report.downloads.docx || downloading !== null} style={{ ...secondaryBtn, opacity: report.downloads.docx ? 1 : 0.5 }}>
                    {downloading?.id === report.id && downloading.format === "docx" ? "Preparing…" : "Download Word"}
                  </button>
                </>
              )}
              {generating ? (
                <button
                  onClick={cancelGeneration}
                  style={{ ...btnPrimary, background: "transparent", border: `1px solid ${theme.color.danger}`, color: theme.color.dangerText }}
                >
                  Cancel
                </button>
              ) : canGenerate ? (
                <button onClick={generateReport} style={btnPrimary}>
                  {report ? "Generate new report" : "Generate Executive Report"}
                </button>
              ) : null}
            </div>
          </div>

          {generating && (
            <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: theme.color.subtle }}>
              <span
                style={{
                  display: "inline-block",
                  width: 14,
                  height: 14,
                  border: `2px solid ${theme.color.accent}`,
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              Gathering metrics, writing the narrative and rendering the PDF and Word versions. You can leave this page; the report will be in your history when it is ready.
            </div>
          )}

          {report?.renderError && !generating && (
            <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 3, background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, fontSize: 12, color: theme.color.warningText }}>
              Your document template could not be applied to this report, so the standard style was used. {report.renderError}
            </div>
          )}

          {error && !generating && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: cbSafe("#dc262622"),
                border: cbSafe("1px solid #7f1d1d"),
                borderRadius: 3,
                fontSize: 12,
                color: theme.color.dangerText,
              }}
            >
              {error}
            </div>
          )}
        </div>
      )}

      {/* Previous reports — every generation is stored; always rendered, like the
          compliance check history, so the section is there before the first report */}
      <div style={{ ...card, marginBottom: report && !generating ? 20 : 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>Previous reports</div>
        {historyError && (
          <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 8 }}>
            Could not load the report history — you may need a higher service tier, or the server is unavailable.
          </div>
        )}
        {!historyError && history.length === 0 && !generating && (
          <div style={{ fontSize: 12, color: theme.color.faint, padding: "12px 0" }}>
            No executive reports have been generated yet.{hasCompletedRun ? " Generate one above." : ""}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {generating && (
            <div style={historyRow}>
              <span style={{ display: "inline-block", width: 12, height: 12, border: `2px solid ${theme.color.accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: theme.color.faint, flex: 1 }}>Generating a new report…</span>
              <span style={{ ...historyBadge, background: theme.color.accentBg, color: theme.color.accentText }}>generating</span>
              <button onClick={cancelGeneration} style={{ ...historyBtn, border: `1px solid ${theme.color.danger}`, color: theme.color.dangerText }}>
                Cancel
              </button>
            </div>
          )}
          {history.map((h) => {
            const selected = report?.id === h.id;
            const loading = loadingId === h.id;
            const preparing = (format: "pdf" | "docx") => downloading?.id === h.id && downloading.format === format;
            const badgeStyle: React.CSSProperties = h.renderError
              ? { background: theme.color.warningBg, color: theme.color.warningText }
              : h.styledWithTemplate
                ? { background: theme.color.successBg, color: theme.color.success }
                : { background: theme.color.border, color: theme.color.muted };
            return (
              <div
                key={h.id}
                onClick={() => { if (!selected && !loading) void openReport(h.id); }}
                style={{
                  ...historyRow,
                  borderColor: selected ? theme.color.accent : theme.color.border,
                  background: selected ? theme.color.accentBg : theme.color.panelAlt,
                  cursor: selected ? "default" : "pointer",
                }}
              >
                <span style={{ fontSize: 12, color: selected ? theme.color.heading : theme.color.faint, flex: 1, minWidth: 150 }}>
                  {fmtWhen(h.generatedAt)}
                  {h.generatedBy && <span style={{ color: theme.color.subtle }}> · {h.generatedBy}</span>}
                </span>
                {h.overallScore != null && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: theme.color.accentText }}>{h.overallScore}%</span>
                )}
                <span style={{ ...historyBadge, ...badgeStyle }} title={h.renderError ?? undefined}>
                  {h.renderError ? "template not applied" : h.styledWithTemplate ? "your template" : "standard style"}
                </span>
                <button
                  disabled={!h.downloads.pdf || downloading !== null}
                  onClick={(e) => { e.stopPropagation(); void download("pdf", h.id); }}
                  style={{ ...historyBtn, opacity: h.downloads.pdf ? 1 : 0.4, cursor: h.downloads.pdf ? "pointer" : "not-allowed" }}
                >
                  {preparing("pdf") ? "Preparing…" : "PDF"}
                </button>
                <button
                  disabled={!h.downloads.docx || downloading !== null}
                  onClick={(e) => { e.stopPropagation(); void download("docx", h.id); }}
                  style={{ ...historyBtn, opacity: h.downloads.docx ? 1 : 0.4, cursor: h.downloads.docx ? "pointer" : "not-allowed" }}
                >
                  {preparing("docx") ? "Preparing…" : "Word"}
                </button>
                <span style={{ fontSize: 11, color: theme.color.accentText, minWidth: 52, textAlign: "right" }}>
                  {loading ? "Loading…" : selected ? "Viewing" : "View →"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scheduled board pack (components/BoardPackSchedule.tsx): reports:generate only, as the API requires. */}
      {canGenerate && <BoardPackSchedule />}

      {/* Report output */}
      {report && !generating && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <ExecutiveReportDashboard m={report.metrics} />

          {/* Narrative sections */}
          {sections.map((section, i) => (
            <div key={i} style={card}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: theme.color.heading,
                  marginBottom: 12,
                  paddingBottom: 10,
                  borderBottom: `1px solid ${theme.color.border}`,
                }}
              >
                {section.heading}
              </div>
              <div>{renderBody(section.body)}</div>
            </div>
          ))}
        </div>
      )}

        </>
      )}

      {activeTab === "gaps" && (
        <GapAnalysisTab
          soaData={soaData}
          soaLoading={soaLoading}
          soaError={soaError}
          enrolledFrameworkIds={enrolledFrameworkIds}
          onDownloadPdf={downloadGapPdf}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
